import React, { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Bell, X, Clock, CheckCircle2, XCircle, Download, DollarSign,
  ChevronDown, Settings, ArrowLeft,
  Globe, UserPlus, Trash2, Camera, BellOff, RefreshCw, StickyNote, Handshake, Star,
} from 'lucide-react';
import { initPush, getPushStatus, type PushStatus } from '../services/pushService';
import { Notification, TripConfig } from '../types';
import { notificationItemIcon, l1KeyForType } from '../constants/taxonomy';
import { distanceToNow } from '../utils/relativeTime';
import { doc, updateDoc, deleteDoc, getDoc, writeBatch } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { notifyInviteAccepted } from '../services/notificationService';
import { respondToTrip } from '../services/tripMembership';
import { JoinTripDates } from './TripParticipation';
import type { TripParticipation } from '../../functions/src/travellerRoutePolicy.mjs';
import { getPendingInvitations } from '../services/userLookup';
import { UserAvatar } from './UserAvatar';
import { CardTintStreaks, PaperTexture } from './UI';
import { userMessage } from '../utils/userMessage';
import { openDeepLink } from '../utils/deepLinks';

interface NotificationCenterProps {
  notifications: Notification[];
  onClose: () => void;
  userId: string;
  /** All trips the user belongs to — used for live destination-emoji lookups + trip switcher. */
  trips?: TripConfig[];
  /** Currently active trip in the main app — used to pre-select the trip tab. */
  activeTripId?: string;
  /** Called when user switches trip in the notification panel. */
  onSwitchTrip?: (tripId: string) => void;
  onOpenImport?: (pendingImportId: string) => void;
  /** Imports still awaiting review; a reviewed one shows no "Review booking". */
  pendingImportIds?: Set<string>;
  /** branchId: a private-branch notification opens in that branch (ignored unless you can open it). */
  onNavigate?: (tab: string, branchId?: string) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Renders a notification message string.
 * Supports **bold** and *bold* (font-semibold, darker text), _italic_ (italic, sub colour).
 * Single *asterisk* phrases are bolded — the original convention used in notification copy.
 */
type MessageToken = { kind: 'text' | 'strong' | 'em'; text: string };

/**
 * Split a notification message into its markup runs.
 *
 * Two rules, and the underscore one is the fussy half. `**bold**` marks the
 * thing that changed; `_emphasis_` marks the trip it changed in, and the trip
 * name is whatever the traveller typed. A trip called `Camp_2026_Reunion`
 * inside `_..._` used to match at the FIRST inner underscore, so the emphasis
 * closed early and the rest of the name spilled into the sentence carrying
 * bare underscores.
 *
 * So an underscore only opens a run at a boundary — start of the message, or
 * after a space or an opening bracket — and only closes at one. An underscore
 * inside a word is part of the word, and a DOUBLED underscore opens nothing at
 * all, which keeps the `__settled__` sentinel emojis out of this even if one
 * ever found its way into message copy.
 */
const MESSAGE_MARKUP = /\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|(^|[\s(["'])_([^\n_][^\n]*?)_(?=$|[\s.,!?;:)\]"'])/g;

function messageTokens(msg: string): MessageToken[] {
  const text = msg || '';
  const tokens: MessageToken[] = [];
  const push = (kind: MessageToken['kind'], value: string) => { if (value) tokens.push({ kind, text: value }); };
  let cursor = 0;
  for (const match of text.matchAll(MESSAGE_MARKUP)) {
    const start = match.index ?? 0;
    push('text', text.slice(cursor, start));
    if (match[1] !== undefined) push('strong', match[1]);
    else if (match[2] !== undefined) push('strong', match[2]);
    else {
      // The boundary character belongs to the sentence, not the emphasis.
      push('text', match[3]);
      push('em', match[4]);
    }
    cursor = start + match[0].length;
  }
  push('text', text.slice(cursor));
  return tokens;
}

function formatMessage(msg: string) {
  return messageTokens(msg).map((token, i) => {
    if (token.kind === 'strong') return <strong key={i} className="font-semibold text-app-text">{token.text}</strong>;
    // Italic, and only italic. This used to carry the upright utility AND the
    // italic one — two that cancel, so which won came down to stylesheet order
    // — on a span whose colour already matched the surrounding text. Whichever
    // way that landed, the emphasis either did nothing or did it by accident.
    // Bold takes darker; this takes slant, in the sentence's own muted tone.
    if (token.kind === 'em') return <em key={i} className="italic text-app-sub">{token.text}</em>;
    return <span key={i} className="text-app-sub">{token.text}</span>;
  });
}

/**
 * Resolves the icon for a notification using this priority order:
 *
 * 1. Traveller notifications (invite / joined / declined) → gold person icon
 * 2. Destination notifications → live emoji from trip config (falls back to
 *    stored emoji, then globe) so renaming a destination emoji auto-updates
 * 3. Subcategory emoji stored in data.emoji (transport mode, accommodation
 *    category, expense category, attraction/eat emoji, etc.) → emoji character
 * 4. Title-based detection — catches legacy notifications where category may be wrong
 * 5. Tab-level fallback icon, gold, matching the app's navigation tabs
 */
function categoryIcon(n: Notification, trips?: TripConfig[]) {
  const cat = (n as any).category ?? n.type;
  const title = n.title.toLowerCase();

  // ── 1. Traveller actions ────────────────────────────────────────────────────
  if (
    n.type === 'invite' ||
    title.includes('joined')     ||
    title.includes('declined')   ||
    title.includes('invitation') ||
    (cat === 'trip-admin' && title.includes('member'))
  ) {
    return <UserPlus className="w-4 h-4 text-gold" />;
  }

  // ── 2. Destination notifications — live emoji lookup ────────────────────────
  if (cat === 'trip-admin' && title.includes('destination')) {
    const tripId = n.data?.tripId;
    const destName = n.data?.destName;
    if (tripId && destName && trips) {
      const trip = trips.find(t => t.id === tripId);
      const dest = trip?.destinations?.find(d => d.name === destName);
      if (dest?.emoji) return <span className="text-lg leading-none">{dest.emoji}</span>;
    }
    if (n.data?.emoji) return <span className="text-lg leading-none">{n.data.emoji}</span>;
    return <Globe className="w-4 h-4 text-gold" />;
  }

  // ── 3. Sentinel icons rendered as Lucide (must match the itinerary section) ──
  if (n.data?.emoji === '__added__')   return <CheckCircle2 className="w-4 h-4 text-gold" />;
  if (n.data?.emoji === '__removed__') return <BellOff      className="w-4 h-4 text-gold" />;
  if (n.data?.emoji === '__settled__') return <Handshake    className="w-4 h-4 text-gold" />;
  // Notes — same StickyNote icon as the itinerary Notes section. '📝' kept for
  // back-compat with note notifications written before the sentinel existed.
  if (n.data?.emoji === '__note__' || n.data?.emoji === '📝') return <StickyNote className="w-4 h-4 text-gold" />;

  // ── 4. Subcategory emoji (hotel, restaurant, etc.) — subtle opacity ─────────
  if (n.data?.emoji) {
    const Icon = notificationItemIcon(n.data.emoji, l1KeyForType(n.data.deepLink?.section || ''));
    if (Icon) return <Icon className="w-4 h-4 text-gold opacity-60" aria-hidden="true" strokeWidth={1.7} />;
    return <span className="text-lg leading-none opacity-50">{n.data.emoji}</span>;
  }

  // ── 5. Title-based legacy detection ────────────────────────────────────────
  if (title.includes('memory') || title.includes('liked your memory'))
    return <Camera className="w-4 h-4 text-gold" />;
  if (
    title.includes('expense')  ||
    title.includes('payment')  ||
    title.includes('split')    ||
    title.includes('settled')  ||
    title.includes('currency')
  ) return <DollarSign className="w-4 h-4 text-gold" />;

  // ── 6. Category/type tab-level fallbacks ────────────────────────────────────
  if (cat === 'trip-admin')                          return <Globe       className="w-4 h-4 text-gold" />;
  if (cat === 'itinerary')                           return <Globe       className="w-4 h-4 text-gold" />;
  if (cat === 'shortlist')                           return <Star        className="w-4 h-4 text-gold" />;
  if (cat === 'expenses' || n.type === 'expense')    return <DollarSign  className="w-4 h-4 text-gold" />;
  if (cat === 'import'   || n.type === 'import')     return <Download    className="w-4 h-4 text-gold" />;
  if (cat === 'social')                              return <Camera      className="w-4 h-4 text-gold" />;
  if (cat === 'reminder')                            return <Clock       className="w-4 h-4 text-gold" />;

  return <Globe className="w-4 h-4 text-gold" />;
}

function categoryBg(_n: Notification) {
  return 'bg-app-border/50';
}

/**
 * The membership callable only accepts an active invitation for this account.
 * Explain a permission refusal as an expired invitation, not raw Firebase copy.
 */
function inviteActionError(action: 'accept' | 'decline', err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  // "declineed" — `${action}ed` works for exactly one of the two, and this is
  // the sentence someone reads at the moment the invite already failed them.
  const past = action === 'accept' ? 'accepted' : 'declined';
  if (/permission|insufficient/i.test(message)) {
    return `This invitation is no longer active on the trip, so it can't be ${past}. `
      + 'Ask whoever invited you to send it again.';
  }
  return `Couldn't ${action} the invite: ${userMessage(err, 'check your connection and try again.')}`;
}

/**
 * Derive the effective lifecycle state from a notification.
 *
 * IMPORTANT: the `read` boolean is the source of truth for read/unread, because
 * Firestore security rules currently only permit clients to mutate `read` — not
 * `state`. The explicit `state` field is consulted only for the terminal values
 * 'actioned' and 'dismissed', which are written by invite/import flows that
 * have their own rule coverage.
 */
function getNotifState(n: Notification): 'unread' | 'read' | 'actioned' | 'dismissed' {
  const explicit = (n as any).state;
  if (explicit === 'actioned') return 'actioned';
  if (explicit === 'dismissed') return 'dismissed';
  return n.read ? 'read' : 'unread';
}

function isActionRequired(n: Notification) {
  const state = getNotifState(n);
  return ((n as any).actionRequired ?? false) && state !== 'actioned' && state !== 'dismissed';
}

// ─── Single notification row ──────────────────────────────────────────────────

function NotifItem({
  n, userId, trips, onNavigate, onOpenImport, pendingImportIds, localReadIds,
  onDismiss, onAction, onRead, onSeen, onAcceptInvite, onDeclineInvite,
}: {
  key?: React.Key;
  n: Notification;
  userId: string;
  trips?: TripConfig[];
  /** branchId: a private-branch notification opens in that branch (ignored unless you can open it). */
  onNavigate?: (tab: string, branchId?: string) => void;
  onOpenImport?: (id: string) => void;
  pendingImportIds?: Set<string>;
  localReadIds: Set<string>;
  onDismiss: (id: string) => void | Promise<void>;
  onAction: (id: string) => void | Promise<void>;
  onRead: (id: string) => void | Promise<void>;
  /** Reported once this row has genuinely been on screen for a moment. */
  onSeen: (id: string) => void;
  onAcceptInvite: (n: Notification) => void | Promise<void>;
  onDeclineInvite: (n: Notification) => void | Promise<void>;
}) {
  const [isRevealed, setIsRevealed] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const state = getNotifState(n);
  // A notification is unread only if Firestore says so AND we haven't
  // locally marked it read yet (optimistic update from the 2-second timer).
  const unread = state === 'unread' && !localReadIds.has(n.id);
  const pending = isActionRequired(n) && state === 'read';
  const hasDeepLink = !!(n.data?.deepLink?.tab);

  // Track drag distance so a tap on the body doesn't fire while swiping
  const dragOffsetX = useRef(0);

  /**
   * Mark this one read once it has actually been looked at — half on screen
   * for three quarters of a second, which a scroll straight past doesn't
   * satisfy. Deliberately not applied to anything still awaiting a decision:
   * for those the marker is the only thing carrying "you still need to deal
   * with this", so it stays until the row is opened.
   */
  useEffect(() => {
    const el = rowRef.current;
    if (!el || !unread || isActionRequired(n) || typeof IntersectionObserver === 'undefined') return;
    let dwell: ReturnType<typeof setTimeout> | null = null;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!dwell) dwell = setTimeout(() => onSeen(n.id), 750);
      } else if (dwell) {
        clearTimeout(dwell);
        dwell = null;
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => { observer.disconnect(); if (dwell) clearTimeout(dwell); };
  }, [n, unread, onSeen]);

  const handleDeepLink = () => {
    const deepLink = n.data?.deepLink;
    if (deepLink?.tab) onNavigate?.(deepLink.tab, n.data?.branchId);
    // Opens the item itself (shared with the push path; handles old tab names).
    if (deepLink) openDeepLink(deepLink);
    onAction(n.id);
  };

  const handleBodyClick = () => {
    // Ignore if the user was swiping
    if (Math.abs(dragOffsetX.current) > 8) return;
    if (unread) onRead(n.id);
    if (hasDeepLink && n.type !== 'invite' && n.type !== 'import') {
      handleDeepLink();
    }
  };

  const senderName = (n.data?.senderName || '').split(' ')[0];

  return (
    <div ref={rowRef} className="relative overflow-hidden rounded-2xl border border-app-border/40 shadow-sm bg-white">
      {/* Background Dismiss Layer */}
      <div className="absolute inset-0 bg-rose-500 flex items-center justify-end px-6">
        <button
          onClick={() => onDismiss(n.id)}
          className="flex flex-col items-center gap-1 text-white opacity-80 hover:opacity-100 transition-opacity"
        >
          <Trash2 className="w-4 h-4" />
          <span className="text-[11px] font-black uppercase tracking-widest">Dismiss</span>
        </button>
      </div>

      {/* Foreground Content */}
      <motion.div
        drag="x"
        dragConstraints={{ left: -80, right: 0 }}
        dragElastic={0.1}
        onDrag={(_, info) => { dragOffsetX.current = info.offset.x; }}
        onDragEnd={(_, info) => {
          if (!isRevealed && info.offset.x < -40) {
            setIsRevealed(true);
          } else if (isRevealed && info.offset.x > 30) {
            setIsRevealed(false);
          } else if (!isRevealed && info.offset.x >= -40) {
            setIsRevealed(false);
          } else if (isRevealed && info.offset.x <= 30) {
            setIsRevealed(true);
          }
          // Reset after a short delay so click handlers fire correctly
          setTimeout(() => { dragOffsetX.current = 0; }, 80);
        }}
        animate={{ x: isRevealed ? -80 : 0 }}
        transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}
        className={`p-4 relative bg-white transition-colors cursor-grab active:cursor-grabbing ${unread ? 'bg-gold/[0.025]' : ''} ${pending ? 'bg-amber-50/30' : ''}`}
      >
        {unread && <div className="absolute top-4 right-4 w-1.5 h-1.5 bg-gold rounded-full" />}

        {/* Clickable body — navigates on tap, ignored during swipe */}
        <div
          onClick={handleBodyClick}
          className={`flex gap-3 ${hasDeepLink && n.type !== 'invite' && n.type !== 'import' ? 'cursor-pointer' : ''}`}
        >
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${categoryBg(n)}`}>
            {categoryIcon(n, trips)}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 mb-1.5">
              <p className="text-sm leading-relaxed flex-1 pr-4">
                {formatMessage(n.message)}
              </p>
              {pending && (
                <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full shrink-0 mt-0.5">
                  Pending
                </span>
              )}
            </div>

            {/* Invite CTAs */}
            {n.type === 'invite' && state !== 'actioned' && (
              <div className="flex gap-2 mb-2.5">
                <button
                  onClick={(e) => { e.stopPropagation(); onAcceptInvite(n); }}
                  className="flex-1 py-2.5 rounded-xl border border-gold/40 bg-gold/5 text-gold hover:bg-gold/10 text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> Accept
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDeclineInvite(n); }}
                  className="flex-1 py-2.5 rounded-xl border border-app-border bg-white/70 text-app-sub hover:text-rose-500 hover:border-rose-300 text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition"
                >
                  <XCircle className="w-3.5 h-3.5 shrink-0" /> Decline
                </button>
              </div>
            )}

            {/* Import CTA — trip is chosen during the review process */}
            {n.type === 'import' && n.data?.pendingImportId && pendingImportIds && !pendingImportIds.has(n.data.pendingImportId) && (
              <div className="mb-2.5 inline-flex items-center gap-1.5 text-xs text-app-sub">
                <CheckCircle2 className="w-3.5 h-3.5" /> Reviewed
              </div>
            )}
            {n.type === 'import' && n.data?.pendingImportId && (!pendingImportIds || pendingImportIds.has(n.data.pendingImportId)) && (
              <div className="mb-2.5">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenImport?.(n.data!.pendingImportId);
                    onAction(n.id);
                  }}
                  className="bg-gold hover:brightness-110 text-white text-[11px] font-black uppercase tracking-widest px-4 py-2 rounded-lg flex items-center gap-1.5 transition"
                >
                  <Download className="w-3 h-3" /> Review booking
                </button>
              </div>
            )}

            {/* Attribution row: sender avatar + name + timestamp */}
            <div className="flex items-center gap-1 mt-1 overflow-hidden">
              {n.data?.creatorId && senderName && (
                <>
                  <UserAvatar userId={n.data.creatorId} size={12} />
                  <span className="text-xs font-medium text-app-sub/40 truncate">{senderName}</span>
                  <span className="text-app-border/40 text-xs shrink-0">·</span>
                </>
              )}
              <Clock className="w-2.5 h-2.5 text-app-sub/40 shrink-0" />
              <span className="text-xs font-medium text-app-sub/40 truncate">
                {distanceToNow(n.createdAt)} ago
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Collapsible section ──────────────────────────────────────────────────────

function Section({
  label, icon, count, unreadCount, defaultOpen = true, children,
}: {
  label: string;
  icon?: ReactNode;
  count: number;
  unreadCount?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;
  const badge = unreadCount !== undefined && unreadCount > 0 ? unreadCount : null;
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 pt-4 pb-2 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="ds-label">{label}</span>
          {badge !== null && (
            <span className="text-[10px] font-bold text-white bg-gold rounded-full px-2 py-0.5">{badge}</span>
          )}
        </div>
        <ChevronDown className={`w-3 h-3 text-app-sub/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.16 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-2 space-y-2.5">
              <AnimatePresence mode="popLayout">{children}</AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function NotificationCenter({
  notifications, onClose, userId, trips, activeTripId,
  onSwitchTrip, onOpenImport, pendingImportIds, onNavigate,
}: NotificationCenterProps) {
  const [activeFilter, setActiveFilter] = useState<'All' | 'Trip Admin' | 'Itinerary' | 'Shortlist' | 'Expenses'>('All');

  // Trip switcher — stays in sync with the active trip in the main app
  const [selectedTripId, setSelectedTripId] = useState(activeTripId || trips?.[0]?.id || '');

  // Keep in lock-step when the main app switches trips (e.g. user picks a different trip
  // in the main view while notifications is open, or opens notifications after switching)
  useEffect(() => {
    if (activeTripId && activeTripId !== selectedTripId) {
      setSelectedTripId(activeTripId);
      setActiveFilter('All' as const);
    }
  }, [activeTripId]);

  // ── Optimistic read tracking ──────────────────────────────────────────────────
  // Tracks IDs we've locally marked as read so the dot disappears immediately,
  // without waiting for the Firestore round-trip.
  const [localReadIds, setLocalReadIds] = useState<Set<string>>(new Set());

  // Keep refs to the latest notifications and selectedTripId so timers always
  // see current values without being in the dep array.
  const notificationsRef = useRef(notifications);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);

  // Clear invitations that can no longer be accepted. A host withdrawing one
  // revokes it directly (revokeTripInvite), but an invite also goes stale if
  // The trip document contains member profiles, so pending invitees check
  // only their invitation IDs through the narrow callable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pending = notificationsRef.current.filter(n =>
        n.type === 'invite' && isActionRequired(n) && n.data?.tripId);
      if (!pending.length) return;
      try {
        const activeIds = new Set((await getPendingInvitations()).map(invite => invite.tripId));
        if (cancelled) return;
        for (const n of pending) if (!activeIds.has(n.data!.tripId)) await dismiss(n.id);
      } catch (error) {
        // A transient lookup failure must not erase valid invitations.
        console.error('Could not check pending invitations:', error);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After viewing a trip's notifications for 2 seconds, mark only that trip's
  // unread notifications as read. Re-runs when the user switches trips so each
  // trip's notifications are only marked read when actually viewed.
  /**
   * Marking things read by what the traveller has actually looked at.
   *
   * This used to be a blanket two-second timer that marked every unread
   * notification belonging to the *selected trip*. Two problems. It cleared
   * notifications far below the fold that nobody had seen; and the trip
   * filter didn't match the filter that decides what's on screen, so a
   * notification could be listed and permanently excluded from ever being
   * marked read — which is the "they don't clear" report.
   *
   * Each row now reports when it's been on screen for a moment (see
   * NotificationRow's observer) and those ids are flushed here in one batch.
   * Anything still awaiting a decision is excluded: an invitation stays
   * marked until it's actually opened, because the marker is the only thing
   * carrying "you still need to deal with this".
   */
  const seenBuffer = useRef<Set<string>>(new Set());
  const seenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markSeen = useCallback((id: string) => {
    if (seenBuffer.current.has(id)) return;
    seenBuffer.current.add(id);
    if (seenTimer.current) clearTimeout(seenTimer.current);
    seenTimer.current = setTimeout(() => {
      const ids = [...seenBuffer.current];
      seenBuffer.current.clear();
      if (!ids.length) return;
      // Optimistic: accumulate, never replace — earlier reads must survive.
      setLocalReadIds(prev => new Set([...prev, ...ids]));
      const batch = writeBatch(db);
      ids.forEach(one => batch.update(doc(db, 'users', userId, 'notifications', one), { read: true }));
      batch.commit().catch(err => console.error('[NotificationCenter] batch markRead failed:', err));
    }, 500);
  }, [userId]);

  useEffect(() => () => { if (seenTimer.current) clearTimeout(seenTimer.current); }, []);

  const dismiss = (id: string) => deleteDoc(doc(db, 'users', userId, 'notifications', id)).catch(console.error);
  const markActioned = (id: string) =>
    // Write `read` first (always allowed by rules) so the badge clears even
    // if the `state` field write is rejected by older rule deployments.
    updateDoc(doc(db, 'users', userId, 'notifications', id), { read: true, state: 'actioned' })
      .catch(() => updateDoc(doc(db, 'users', userId, 'notifications', id), { read: true })
        .catch(err => console.error('[NotificationCenter] markActioned failed:', err)));
  const markRead = (id: string) =>
    updateDoc(doc(db, 'users', userId, 'notifications', id), { read: true })
      .catch(err => console.error('[NotificationCenter] markRead failed:', err));

  const [joiningInvite, setJoiningInvite] = useState<Notification | null>(null);
  const handleAcceptInvite = async (n: Notification, dates: TripParticipation | null) => {
    if (!n.data?.tripId) return;
    try {
      const outcome = await respondToTrip(n.data.tripId, 'accept', undefined, dates);

      if (outcome === 'gone') {
        await dismiss(n.id);
        setJoiningInvite(null);
        return;
      }

      if (outcome === 'done') {
        // Membership is now accepted; reading the trip and notifying its
        // members is allowed only after the server commits the acceptance.
        const tripSnap = await getDoc(doc(db, 'trips', n.data.tripId));
        const tripData = tripSnap.data() as TripConfig;
        await notifyInviteAccepted(
          n.data.tripId,
          n.data.tripTitle || 'the trip',
          userId,
          auth.currentUser?.displayName?.split(' ')[0] || 'Someone',
          [...(tripData.memberIds || []), userId]
        ).catch(console.error);
      }

      // Delete the invite notification once a selection has been made — the
      // accept/decline action is terminal and the row should disappear from
      // the centre rather than linger in an 'actioned' state.
      await dismiss(n.id);
      onClose();
    } catch (err) {
      console.error('Error accepting invite:', err);
      throw new Error(inviteActionError('accept', err));
    }
  };

  const handleDeclineInvite = async (n: Notification) => {
    if (!n.data?.tripId) return;
    try {
      await respondToTrip(n.data.tripId, 'decline', n.id);
      await dismiss(n.id);
      onClose();
    } catch (err) {
      // Previously console-only, so a failed decline looked like a dead
      // button. Same surfacing as accept.
      console.error('Error declining invite:', err);
      alert(inviteActionError('decline', err));
    }
  };

  // ── Unread counts per trip (for switcher badges) ───────────────────────────
  const unreadByTrip = (trips || []).reduce<Record<string, number>>((acc, trip) => {
    acc[trip.id] = notifications.filter(n =>
      n.data?.tripId === trip.id &&
      getNotifState(n) === 'unread' &&
      !localReadIds.has(n.id)
    ).length;
    return acc;
  }, {});

  // ── Filter pipeline ────────────────────────────────────────────────────────
  // 1. Exclude dismissed
  let visible = notifications.filter(n => getNotifState(n) !== 'dismissed');

  // 2. Trip filter (always active — one trip at a time, switchable via the
  //    in-panel trip switcher which only lists the user's accessible trips).
  //
  //    A notification is trip-scoped ONLY when its trip is one the user can
  //    actually select. Otherwise it can never be reached through the switcher
  //    and must always show — the badge counts it (App.tsx unreadCount has no
  //    trip filter) so hiding it produces the "red dot + All caught up" bug.
  //    This covers, generically:
  //      • invites          — data.tripId is a trip the recipient hasn't joined
  //      • trip-deleted      — the cited trip was deleted, so it's gone from trips
  //      • unassigned imports — no tripId until reviewed
  //      • any tripless notification
  //    Anything whose trip IS accessible stays scoped to the selected trip.
  if (selectedTripId) {
    const myTripIds = new Set((trips || []).map(t => t.id));
    visible = visible.filter(n => {
      const tid = n.data?.tripId;
      if (!tid) return true;                 // tripless (e.g. unassigned import)
      if (!myTripIds.has(tid)) return true;  // orphaned (invite / deleted trip)
      return tid === selectedTripId;         // normal: scoped to selected trip
    });
  }

  // 3. Category filter
  // Import notifications are always shown regardless of active filter (same
  // reason as trip filter — they are action-required and shouldn't be buried).
  if (activeFilter !== 'All') {
    visible = visible.filter(n => {
      const cat = (n as any).category ?? n.type;
      if (activeFilter === 'Trip Admin') return cat === 'trip-admin' || cat === 'import' || n.type === 'invite' || n.type === 'import';
      if (activeFilter === 'Itinerary')  return cat === 'itinerary';
      if (activeFilter === 'Shortlist')  return cat === 'shortlist';
      if (activeFilter === 'Expenses')   return cat === 'expenses'  || n.type === 'expense';
      return true;
    });
  }

  // 4. Sort — most recent first
  const sortedVisible = [...visible].sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

  const isEffectivelyUnread = (n: Notification) =>
    getNotifState(n) === 'unread' && !localReadIds.has(n.id);

  const visibleUnreadCount = visible.filter(isEffectivelyUnread).length;

  const actionRequired = sortedVisible.filter(isActionRequired);
  const recent = sortedVisible.filter(n =>
    !isActionRequired(n) &&
    new Date(n.createdAt).getTime() >= sevenDaysAgo
  );
  const earlier = sortedVisible.filter(n =>
    !isActionRequired(n) &&
    new Date(n.createdAt).getTime() < sevenDaysAgo
  );

  const sharedProps = {
    pendingImportIds,
    userId, trips, onNavigate, onOpenImport, localReadIds, onSeen: markSeen,
    onDismiss: dismiss, onAction: markActioned, onRead: markRead,
    onAcceptInvite: (n: Notification) => setJoiningInvite(n), onDeclineInvite: handleDeclineInvite,
  };

  // ── Push notification preferences ────────────────────────────────────────────
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState<Record<string, boolean>>({
    'trip-admin': true, itinerary: true, shortlist: true, expenses: true, social: true,
  });
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [pushStatus, setPushStatus] = useState<PushStatus>(getPushStatus);
  const [pushLoading, setPushLoading] = useState(false);

  // Refresh push status whenever the prefs panel opens.
  useEffect(() => {
    if (!showPrefs) return;
    initPush(userId).then(status => setPushStatus(status));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPrefs]);

  const handleEnablePush = async () => {
    setPushLoading(true);
    const status = await initPush(userId);
    setPushStatus(status);
    setPushLoading(false);
  };

  const handleOpenPrefs = async () => {
    setShowPrefs(true);
    setPrefsLoading(true);
    try {
      const snap = await getDoc(doc(db, 'users', userId));
      const saved = snap.data()?.notifPrefs as Record<string, boolean> | undefined;
      if (saved) setPrefs(prev => ({ ...prev, ...saved }));
    } catch (e) {
      console.error('[NotifPrefs] load failed', e);
    } finally {
      setPrefsLoading(false);
    }
  };

  const handleTogglePref = async (key: string, next: boolean) => {
    const updated = { ...prefs, [key]: next };
    setPrefs(updated);
    updateDoc(doc(db, 'users', userId), { notifPrefs: updated })
      .catch(e => { console.error('[NotifPrefs] save failed', e); setPrefs(prefs); });
  };

  // Composite icon: Globe with a small badge (! or +) in the top-right corner
  const GlobeBadge = ({ badge }: { badge: '!' | '+' }) => (
    <div className="relative">
      <Globe className="w-5 h-5 text-gold" />
      <span className="absolute -top-1 -right-0.5 w-2.5 h-2.5 bg-gold rounded-full flex items-center justify-center text-[8px] font-black text-white leading-none">
        {badge}
      </span>
    </div>
  );

  const PREF_CATEGORIES = [
    { key: 'trip-admin', label: 'Trip updates & admin', desc: 'Members, dates, destinations, booking & itinerary imports', icon: <GlobeBadge badge="!" /> },
    { key: 'itinerary',  label: 'Itinerary',             desc: 'Transport and accommodation',                              icon: <GlobeBadge badge="+" /> },
    { key: 'shortlist',  label: 'Shortlist',             desc: 'Shortlisted ideas, items added to plan, day notes',        icon: <Star className="w-5 h-5 text-gold" /> },
    { key: 'expenses',   label: 'Expenses',               desc: 'Splits, payments, settlements',                            icon: <DollarSign className="w-5 h-5 text-gold" /> },
  ] as const;

  // No Memories filter while there's no Memories tab to send anyone to —
  // social/reminder notifications still arrive and still show under All.
  // Reintroduce alongside whatever Memories becomes (BACKLOG.md).
  const FILTERS = ['All', 'Trip Admin', 'Itinerary', 'Shortlist', 'Expenses'] as const;
  const showTripSwitcher = (trips?.length ?? 0) > 1;

  const handleTripSwitch = (tripId: string) => {
    setSelectedTripId(tripId);
    setActiveFilter('All');
    onSwitchTrip?.(tripId);
  };

  // Full screen rather than a card on a backdrop: this is a list you work
  // through — accepting invites, reading a trip's history, switching trips —
  // not a message you dismiss, and the 85vh card it briefly was gave the list
  // less room than the slide-over it replaced.
  //
  // Still createPortal, and still no AnimatePresence around it. The original
  // was `position: fixed` inside the app's own scrolled tree, which on an iOS
  // PWA pins it to that ancestor instead of the viewport — the reason it
  // rendered as a short, offset panel floating over the page.
  //
  // There is no backdrop to tap, so the header's X is the only way out; it sits
  // inside the safe area rather than under the notch.
  return createPortal(
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[100] bg-app-bg flex flex-col overflow-hidden"
    >
      <div className="flex flex-col flex-1 min-h-0">

      {/* Prefs and notification list swap at the top of the panel */}
      <AnimatePresence mode="wait">
        {showPrefs ? (
          <motion.div
            key="prefs"
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="flex-1 flex flex-col min-h-0 bg-app-bg"
          >
            {/* Prefs header */}
            <div className="pt-[calc(1rem+env(safe-area-inset-top))] pb-3 px-5 border-b border-app-border/40 flex items-center gap-3 bg-app-bg shrink-0">
              <button aria-label="Back" onClick={() => setShowPrefs(false)} className="p-1.5 hover:bg-app-faint rounded-full transition-colors">
                <ArrowLeft className="w-4 h-4 text-app-sub" />
              </button>
              <Settings className="w-4 h-4 text-gold" />
              <h2 className="text-sm font-black uppercase tracking-widest text-app-text">Push Settings</h2>
            </div>

            {/* Prefs body */}
            <div className="relative flex-1 min-h-0 overflow-hidden" style={{ contain: 'paint' }}>
            <PaperTexture />
            <CardTintStreaks />
            <div className="relative z-10 h-full overflow-y-auto no-scrollbar px-5 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">

              {/* Push device status card */}
              <div className={`mb-5 rounded-2xl border px-4 py-3.5 shadow-sm ${pushStatus === 'registered' ? 'bg-white border-gold/30' : 'bg-white border-gold/40'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2.5">
                    {pushStatus === 'registered'
                      ? <Bell className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                      : <BellOff className="w-4 h-4 text-gold/60 mt-0.5 shrink-0" />}
                    <div>
                      <div className="text-[11px] font-black text-gold uppercase tracking-widest">
                        {pushStatus === 'registered' ? 'This device is registered' : 'Push not enabled on this device'}
                      </div>
                      <div className="text-xs text-gold/80 mt-0.5 leading-snug">
                        {pushStatus === 'registered'   && "You'll receive push notifications when the app is closed."}
                        {pushStatus === 'blocked'       && 'Permission blocked. Enable in Settings → Notifications.'}
                        {pushStatus === 'ios-not-pwa'  && 'Add Talisman to your home screen to enable push on iOS.'}
                        {pushStatus === 'unsupported'  && 'Push notifications are not supported on this browser.'}
                        {pushStatus === 'native-unconfigured' && 'Push notifications are not configured in this iOS preview. Your in-app notifications still appear here.'}
                        {pushStatus === 'not-requested' && 'Tap below to enable push notifications on this device.'}
                        {(pushStatus === 'error' || pushStatus === 'no-vapid') && 'There seems to be an error enabling push notifications. Try again later.'}
                      </div>
                    </div>
                  </div>
                </div>
                {pushStatus === 'not-requested' && (
                  <button
                    onClick={handleEnablePush}
                    disabled={pushLoading}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-gold/40 bg-gold/5 text-gold hover:bg-gold/10 text-[10px] font-black uppercase tracking-wider transition disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 shrink-0 ${pushLoading ? 'animate-spin' : ''}`} />
                    {pushLoading ? 'Enabling…' : 'Enable push notifications'}
                  </button>
                )}
              </div>

              <p className="text-sm text-app-sub/80 mb-5 leading-relaxed">
                Choose which activities you would like to receive push notifications for.
              </p>
              {prefsLoading ? (
                <div className="flex justify-center py-8 opacity-40">
                  <div className="w-5 h-5 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="rounded-2xl border border-app-border/40 shadow-sm bg-white px-4">
                  {PREF_CATEGORIES.map(({ key, label, desc, icon }) => (
                    <div key={key} className="flex items-center justify-between py-3.5 border-b border-app-border/20 last:border-0 gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-app-faint flex items-center justify-center shrink-0">
                          {icon}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-app-text">{label}</div>
                          <div className="text-xs text-app-sub/80 mt-0.5">{desc}</div>
                        </div>
                      </div>
                      {/* iOS-style toggle */}
                      <button
                        onClick={() => handleTogglePref(key, !prefs[key])}
                        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${prefs[key] ? 'bg-gold' : 'bg-app-border/60'}`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all ${prefs[key] ? 'left-[calc(100%-1.375rem)]' : 'left-0.5'}`} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ x: '-20%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-20%', opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="flex-1 flex flex-col min-h-0"
          >
            {/* Header */}
            <div className="pt-[calc(1rem+env(safe-area-inset-top))] border-b border-app-border/40 flex flex-col bg-app-bg shrink-0">
              <div className="px-5 pb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-gold" />
                  <h2 className="text-sm font-black uppercase tracking-widest text-app-text">Notifications</h2>
                  {visibleUnreadCount > 0 && (
                    <span className="text-[10px] font-bold text-white bg-gold rounded-full px-2 py-0.5">{visibleUnreadCount}</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <button aria-label="Notification settings" onClick={handleOpenPrefs} className="p-1.5 hover:bg-app-faint rounded-full transition-colors" title="Notification settings">
                    <Settings className="w-4 h-4 text-gold" />
                  </button>
                  <button aria-label="Close" onClick={onClose} className="p-1.5 hover:bg-app-faint rounded-full transition-colors">
                    <X className="w-4 h-4 text-app-sub" />
                  </button>
                </div>
              </div>
              {showTripSwitcher && (
                <div className="px-5 pb-2.5 flex gap-1.5 overflow-x-auto no-scrollbar">
                  {trips!.map(trip => {
                    const isSelected = trip.id === selectedTripId;
                    const unread = unreadByTrip[trip.id] ?? 0;
                    return (
                      <button
                        key={trip.id}
                        onClick={() => handleTripSwitch(trip.id)}
                        className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition-all whitespace-nowrap shrink-0 ${
                          isSelected ? 'bg-white border border-app-border text-gold shadow-sm' : 'border border-transparent text-app-sub/50 hover:text-app-sub'
                        }`}
                      >
                        {trip.title}
                        {!isSelected && unread > 0 && (
                          <span className="w-4 h-4 bg-gold text-white text-[9px] font-black rounded-full flex items-center justify-center leading-none">
                            {unread > 9 ? '9+' : unread}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="px-5 pb-3 flex gap-1.5 overflow-x-auto no-scrollbar">
                {FILTERS.map(f => (
                  <button
                    key={f}
                    onClick={() => setActiveFilter(f)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition-all whitespace-nowrap shrink-0 ${
                      activeFilter === f ? 'bg-white border border-app-border text-gold shadow-sm' : 'border border-transparent text-app-sub/50 hover:text-app-sub'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* List body */}
            <div
              className="relative flex-1 min-h-0 overflow-hidden"
              // contain: paint alongside overflow-hidden — plain overflow can
              // fail to clip a blurred, animated descendant on WebKit once it
              // is on its own compositing layer (the feature card's fix).
              style={{ contain: 'paint' }}
            >
            <PaperTexture />
            <CardTintStreaks />
            <div className="relative z-10 h-full overflow-y-auto no-scrollbar pt-2 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        {visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center opacity-40">
            <Bell className="w-10 h-10 mb-4 text-app-sub/40" />
            <p className="text-[11px] font-black uppercase tracking-widest text-app-sub">
              {activeFilter === 'All' ? 'All caught up' : `No ${activeFilter} notifications`}
            </p>
          </div>
        ) : (
          <>
            <Section label="Action Required" count={actionRequired.length} unreadCount={actionRequired.filter(isEffectivelyUnread).length}>
              {actionRequired.map(n => <NotifItem key={n.id} n={n} {...sharedProps} />)}
            </Section>
            <Section label="Recent" icon={<Clock className="w-3 h-3 text-app-sub/40" />} count={recent.length} unreadCount={recent.filter(isEffectivelyUnread).length}>
              {recent.map(n => <NotifItem key={n.id} n={n} {...sharedProps} />)}
            </Section>
            <Section label="Earlier" count={earlier.length} unreadCount={earlier.filter(isEffectivelyUnread).length} defaultOpen={actionRequired.length + recent.length === 0}>
              {earlier.map(n => <NotifItem key={n.id} n={n} {...sharedProps} />)}
            </Section>
          </>
        )}
            </div>
            </div>{/* /list body */}
          </motion.div>
        )}
      </AnimatePresence>
      {joiningInvite?.data?.tripId && <JoinTripDates tripId={joiningInvite.data.tripId}
        onClose={() => setJoiningInvite(null)} onJoin={dates => handleAcceptInvite(joiningInvite, dates)} />}
      </div>
    </motion.div>,
    document.getElementById('import-modal-root') ?? document.body
  );
}
